'use client';

/**
 * GlassProgressBar Component
 *
 * A branded linear progress bar with neon accent gradients and
 * glass-morphism styling. Wraps MUI LinearProgress with theme-aware
 * track backgrounds, neon glow on the progress edge, and optional
 * label/percentage display.
 *
 * Supports determinate (value 0-100) and indeterminate modes.
 * Respects reduced motion preferences by disabling glow pulse animations.
 */

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, keyframes, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

export interface GlassProgressBarProps {
  /** Progress value 0-100 for determinate mode */
  value?: number;
  /** Enable animated indeterminate bar */
  indeterminate?: boolean;
  /** Neon accent color for gradient fill */
  neonAccent?: NeonAccent;
  /** Label text displayed above the bar */
  label?: string;
  /** Show value percentage text */
  showPercentage?: boolean;
  /** Bar height variant: small=4px, medium=8px */
  size?: 'small' | 'medium';
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Constants
// ============================================================================

const SIZE_MAP = {
  small: 4,
  medium: 8,
} as const;

// ============================================================================
// Keyframe animations
// ============================================================================

const glowPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 1; }
`;

// ============================================================================
// Component
// ============================================================================

export function GlassProgressBar({
  value,
  indeterminate = false,
  neonAccent,
  label,
  showPercentage = false,
  size = 'medium',
  sx,
}: GlassProgressBarProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Resolve neon accent colors
  const neonStyles = neonAccent ? getNeonStyles(neonAccent, isDark) : null;
  const baseColor = neonStyles?.base ?? theme.palette.primary.main;
  const glowColor = neonStyles?.glow ?? alpha(theme.palette.primary.main, 0.5);

  // Clamp value to 0-100 range
  const clampedValue = value != null ? Math.min(100, Math.max(0, value)) : undefined;

  // Determine MUI variant: fall back to indeterminate when no value is provided
  const isIndeterminate = indeterminate || clampedValue == null;
  const variant = isIndeterminate ? 'indeterminate' : 'determinate';

  // Bar height
  const barHeight = SIZE_MAP[size];

  // Track background colors
  const trackBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  const hasHeader = label || showPercentage;

  return (
    <Box sx={sx}>
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          {label && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: 13,
              }}
            >
              {label}
            </Typography>
          )}
          {showPercentage && clampedValue != null && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
                ml: 'auto',
              }}
            >
              {Math.round(clampedValue)}%
            </Typography>
          )}
        </Box>
      )}
      <LinearProgress
        variant={variant}
        value={variant === 'determinate' ? clampedValue : undefined}
        sx={{
          height: barHeight,
          borderRadius: barHeight / 2,
          backgroundColor: trackBg,
          '& .MuiLinearProgress-bar': {
            borderRadius: barHeight / 2,
            background: `linear-gradient(90deg, ${alpha(baseColor, 0.85)} 0%, ${baseColor} 50%, ${alpha(baseColor, 0.85)} 100%)`,
            boxShadow: `0 0 ${barHeight * 2}px ${glowColor}`,
            animation: indeterminate ? undefined : `${glowPulse} 2s ease-in-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          },
        }}
      />
    </Box>
  );
}

GlassProgressBar.displayName = 'GlassProgressBar';

export default GlassProgressBar;
